import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate, useParams } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";

import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import MobileCallBar from "@/components/MobileCallBar";
import CookieBanner from "@/components/CookieBanner";
import ProtectedRoute from "@/components/ProtectedRoute";
import { AuthProvider } from "@/contexts/AuthContext";

import HomePage from "@/pages/HomePage";
import TripDetailPage from "@/pages/TripDetailPage";
import ThankYouPage from "@/pages/ThankYouPage";
import LegalPage from "@/pages/LegalPage";
import BlogListPage from "@/pages/BlogListPage";
import BlogPostPage from "@/pages/BlogPostPage";
import AdminLoginPage from "@/pages/AdminLoginPage";
import AdminDashboardPage from "@/pages/AdminDashboardPage";
import AdminSettingsPage from "@/pages/AdminSettingsPage";
import PresentationAlbertoPage from "@/pages/PresentationAlbertoPage";
import NotFoundPage from "@/pages/NotFoundPage";

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const id = hash.slice(1);
      let tries = 0;
      const tryScroll = () => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        } else if (tries++ < 20) {
          setTimeout(tryScroll, 100);
        }
      };
      tryScroll();
      return;
    }
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname, hash]);
  return null;
}

function LegacyTripRedirect() {
  const { slug } = useParams();
  return <Navigate to={`/viajes-familia-esquiar/${slug}`} replace />;
}

function PublicLayout({ children }) {
  return (
    <>
      <SiteHeader />
      <MobileCallBar />
      <main className="pt-0">{children}</main>
      <SiteFooter />
      <CookieBanner />
    </>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <Routes>
            <Route path="/" element={<PublicLayout><HomePage /></PublicLayout>} />
            <Route path="/viajes-familia-esquiar/:slug" element={<PublicLayout><TripDetailPage /></PublicLayout>} />
            <Route path="/gracias-formulario" element={<PublicLayout><ThankYouPage /></PublicLayout>} />
            <Route path="/aviso-legal" element={<PublicLayout><LegalPage variant="legal" /></PublicLayout>} />
            <Route path="/politica-de-privacidad" element={<PublicLayout><LegalPage variant="privacy" /></PublicLayout>} />
            <Route path="/politica-de-cookies" element={<PublicLayout><LegalPage variant="cookies" /></PublicLayout>} />
            <Route path="/blog" element={<PublicLayout><BlogListPage /></PublicLayout>} />
            <Route path="/blog/:slug" element={<PublicLayout><BlogPostPage /></PublicLayout>} />
            {/* Presentación cliente (noindex, sin cabecera/footer para look de landing) */}
            <Route path="/presentacion-alberto" element={<PresentationAlbertoPage />} />
            {/* Redirect old URL structure to new SEO-friendly one */}
            <Route path="/viajes/:slug" element={<LegacyTripRedirect />} />

            <Route path="/admin/login" element={<AdminLoginPage />} />
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <AdminDashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/configuracion"
              element={
                <ProtectedRoute>
                  <AdminSettingsPage />
                </ProtectedRoute>
              }
            />

            <Route path="*" element={<PublicLayout><NotFoundPage /></PublicLayout>} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </HelmetProvider>
  );
}
